import React from "react";

const pageStyle: React.CSSProperties = {
  maxWidth: 1100,
  margin: "40px auto",
  padding: "24px",
  background: "#071226",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#e6edf3"
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 30,
  fontSize: 14,
  minWidth: 700
};

const thStyle: React.CSSProperties = {
  borderBottom: "1px solid rgba(255,255,255,0.15)",
  padding: "12px",
  textAlign: "left",
  fontWeight: 600,
  color: "#fbbf24"
};

const tdStyle: React.CSSProperties = {
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  padding: "12px",
  color: "#d1d5db"
};

const tdHighlight: React.CSSProperties = {
  ...tdStyle,
  color: "#fbbf24",
};

// Group header row
const tdGroup: React.CSSProperties = {
  padding: "10px 12px 6px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "rgba(148,163,184,0.6)",
  background: "rgba(255,255,255,0.02)",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

// Tick / cross helpers
const YES = <span style={{ color: "#34d399", fontWeight: 700 }}>✔</span>;
const NO  = <span style={{ color: "#4b5563" }}>—</span>;

type Row =
  | { type: "group"; label: string }
  | { type: "row"; feature: string; highlight?: boolean; starter: React.ReactNode; growth: React.ReactNode; insight: React.ReactNode; premium: React.ReactNode };

const ROWS: Row[] = [

  // ── PDF Report ──────────────────────────────────────────────
  { type: "group", label: "PDF Report (export only)" },

  { type: "row", feature: "Report Snapshot",       highlight: true,
    starter: NO,          growth: "Included",    insight: "Included",    premium: NO },
  { type: "row", feature: "AI Generated Summary",  highlight: true,
    starter: NO,          growth: "Included",    insight: "Included",    premium: NO },
  { type: "row", feature: "How to Use Guide",
    starter: NO,          growth: "Included",    insight: "Included",    premium: NO },
  { type: "row", feature: "PDF Export",            highlight: true,
    starter: NO,          growth: YES,           insight: YES,           premium: NO },

  // ── Core Report (always accessible in UI) ───────────────────
  { type: "group", label: "Core Report" },

  { type: "row", feature: "Birth Chart",
    starter: "Basic",     growth: "Full",        insight: "Full",        premium: "Full" },
  { type: "row", feature: "Karmic Overview",       highlight: true,
    starter: "Full",      growth: "Full",        insight: "Full",        premium: "Full" },
  { type: "row", feature: "Personality Traits",
    starter: "Lagna + Moon", growth: "Full",     insight: "Full",        premium: "Full" },
  { type: "row", feature: "Planet in Houses",
    starter: "2 Planets", growth: "Full",        insight: "Full",        premium: "Full" },
  { type: "row", feature: "Karmic Remedies",       highlight: true,
    starter: "Limited",   growth: "Full",        insight: "Full",        premium: "Full" },
  { type: "row", feature: "Awareness",             highlight: true,
    starter: "Demo",      growth: "Full",        insight: "Full",        premium: "Full" },

  // ── Deep Analysis (INSIGHT depth) ───────────────────────────
  { type: "group", label: "Deep Analysis" },

  { type: "row", feature: "Strong & Weak Planets", highlight: true,
    starter: "Demo",      growth: "Summary",     insight: "Full",        premium: "Summary" },
  { type: "row", feature: "Planet Conjunctions",
    starter: "Demo",      growth: "Summary",     insight: "Full",        premium: "Summary" },
  { type: "row", feature: "Yoga Analysis",
    starter: "Demo",      growth: "Summary",     insight: "Full",        premium: "Summary" },
  { type: "row", feature: "Vimshottari Dasha",     highlight: true,
    starter: "Demo",      growth: "Current period", insight: "Full lifetime", premium: "Current period" },

];

export default function PlanFeaturesPage() {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1100, margin: "40px auto", padding: "0 20px" }}>

        <h1 style={{ fontSize: 28, marginBottom: 10 }}>Compare Plans</h1>
        <a href="/chart" style={{ opacity: 0.8, color: "grey" }}>
          ← Back to Reports
        </a>
        <p style={{ opacity: 0.8 }}>
          Each plan unlocks a deeper layer of karmic reflection and awareness.
          This comparison shows exactly what is included in each plan.
        </p>

        <div style={{ color: "#fbbf24", marginTop: 10, marginBottom: 20, fontSize: 14 }}>
          🎉 Launch Offer — Early users receive special pricing
        </div>

        <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
          👉 Swipe right to see all plans
        </div>

        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 8 }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Feature</th>

                <th style={thStyle}>
                  STARTER
                  <div style={{ fontSize: 13, color: "#9ca3af" }}>Free</div>
                </th>

                <th style={thStyle}>
                  GROWTH
                  <div style={{ fontSize: 13 }}>
                    <span style={{ textDecoration: "line-through", color: "#6b7280", marginRight: 6 }}>₹599</span>
                    <span style={{ color: "#fbbf24", fontWeight: 600 }}>₹299</span>
                  </div>
                </th>

                <th style={{ ...thStyle, background: "rgba(251,191,36,0.05)" }}>
                  <div style={{ fontSize: 11, color: "#fbbf24", marginBottom: 4 }}>⭐ Most Popular</div>
                  INSIGHT
                  <div style={{ fontSize: 13 }}>
                    <span style={{ textDecoration: "line-through", color: "#6b7280", marginRight: 6 }}>₹999</span>
                    <span style={{ color: "#fbbf24", fontWeight: 600 }}>₹499</span>
                  </div>
                </th>

                <th style={thStyle}>
                  PREMIUM UNLOCK
                  <div style={{ fontSize: 13 }}>
                    <span style={{ textDecoration: "line-through", color: "#6b7280", marginRight: 6 }}>₹399</span>
                    <span style={{ color: "#fbbf24", fontWeight: 600 }}>₹199</span>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {ROWS.map((row, i) => {
                if (row.type === "group") {
                  return (
                    <tr key={i}>
                      <td colSpan={5} style={tdGroup}>{row.label}</td>
                    </tr>
                  );
                }
                const featureStyle = row.highlight ? tdHighlight : tdStyle;
                return (
                  <tr key={i}>
                    <td style={featureStyle}>{row.feature}</td>
                    <td style={tdStyle}>{row.starter}</td>
                    <td style={tdStyle}>{row.growth}</td>
                    <td style={{ ...tdStyle, background: "rgba(251,191,36,0.02)" }}>{row.insight}</td>
                    <td style={tdStyle}>{row.premium}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 30, fontSize: 13, opacity: 0.7 }}>
          <p><b>Demo</b> — <i>shows a locked sample so you can see what the section covers.</i></p>
          <p><b>Summary</b> — <i>shows a condensed version of the analysis.</i></p>
          <p><b>Full</b> — <i>complete depth derived from your chart and active Dasha.</i></p>
          <p><b>Included (PDF)</b> — <i>appears only in the exported PDF report, not as a UI tab.</i></p>
        </div>

      </div>
    </div>
  );
}
